import { useEffect, useMemo, useState } from "react";

import { DescriptionField } from "../ExerciseFields/DescriptionField";
import { LanguageField } from "../ExerciseFields/LanguageField";
import { NameField } from "../ExerciseFields/NameField";
import { ScenarioField } from "../ExerciseFields/ScenarioField";
import { SchemaField } from "../ExerciseFields/SchemaField";
import { FeedUpField } from "../ExerciseFields/FeedUpField";
import { FeedForwardPosField } from "../ExerciseFields/FeedForwardPosField";
import { FeedForwardNegField } from "../ExerciseFields/FeedForwardNegField";
import { FeedForwardThresholdField } from "../ExerciseFields/FeedForwardThresholdField";
import { ScoreField } from "../ExerciseFields/ScoreField";
import Tooltip from "@kea-mod/common/tooltip";
import { b64EncodeUnicode } from "../../../shared/b64";
import { emptyExercise } from "../../../types/Exercise";
import { useExerciseContext } from "../../../context/ExerciseContext";
import { useNavigate } from "react-router-dom";
import { useModelContext } from "../../../context/ModelContext";
import { useModelEditorModalContext } from "../../../context/ModelEditorModalContext";
import { useTranslation } from "react-i18next";
import { ModelList } from "../../../pages/Modellist";
import { AddModelCard } from "../../Models/AddModelCard";

type Props = {
  assignmentId: any;
};

export const AddExerciseCreationComponent = (props: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const exerciseContext = useExerciseContext();
  const modelContext = useModelContext();
  const modelEditorModalContext = useModelEditorModalContext();

  const [activeExercise, setActiveExercise] = useState(emptyExercise);
  const [exerciseId, setExerciseId] = useState(-1);
  const [exerciseCreated, setExerciseCreated] = useState(false);
  const [language, setLanguage] = useState("");
  const [maxScore, setMaxScore] = useState("0");

  const activeModels = useMemo(() => {
    if (exerciseId === undefined) return [];
    return modelContext.models.filter((model: any) => model.parentId === exerciseId);
  }, [exerciseId, modelContext.models]);

  useEffect(() => {
    exerciseContext.all();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event: any) => {
    let value = event.target.value;

    setActiveExercise({
      ...activeExercise,
      [event.target.name]: value,
    });
  };

  const handleChangeLanguage = (event: any) => {
    setLanguage(event.target.value);
  };

  const handleChangeDescription = (event: any) => {
    setActiveExercise({
      ...activeExercise,
      description: event,
    });
  };

  const handleChangeScenario = (event: any) => {
    setActiveExercise({
      ...activeExercise,
      creation: {
        ...activeExercise.creation,
        scenario: event,
      },
    });
  };

  const handleCheckboxChange = (event: any) => {
    setActiveExercise({
      ...activeExercise,
      [event.target.name]: event.target.checked,
    });
  };

  const emptyCheck = () => {
    if (
      activeExercise.name.trim() === "" ||
      activeExercise.description === "" ||
      activeExercise.creation.scenario === "" ||
      language === "" ||
      activeExercise.schemaId === ""
    ) {
      return true;
    } else {
      return false;
    }
  };

  const addFeedFields = (data: any) => {
    //only add fields to data if not empty
    if (activeExercise.feedUp !== "") {
      data.feedUp = activeExercise.feedUp;
    }

    if (activeExercise.feedForward_pos !== "") {
      data.feedForward_pos = activeExercise.feedForward_pos;
    }

    if (activeExercise.feedForward_neg !== "") {
      data.feedForward_neg = activeExercise.feedForward_neg;
    }

    if (activeExercise.feedForward_threshold !== "") {
      data.feedForward_threshold = parseFloat(activeExercise.feedForward_threshold);
    }
  };

  const exerciseData = () => {
    let data: any = {
      name: activeExercise.name,
      type: "creation",
      language: language,
      description: b64EncodeUnicode(activeExercise.description),
      scenario: b64EncodeUnicode(activeExercise.creation.scenario),
      schemaId: parseInt(activeExercise.schemaId),
      assignmentId: parseInt(props.assignmentId),
      directFeedback: activeExercise.directFeedback,
      maxTotalScore: maxScore,
    };

    addFeedFields(data);

    return data;
  };

  /**
   * A sample solution is stored with a reference to its exercise, so the exercise row has to
   * exist before the editor can be opened. Instead of asking the author to create the exercise
   * in a separate step, it is created here on demand and updated again when the form is saved.
   */
  const ensureExerciseExists = async () => {
    if (exerciseCreated) {
      return exerciseId;
    }

    if (emptyCheck()) {
      alert(t("completeBeforeSampleSolution"));
      return -1;
    }

    let createdId = await createExercise();
    if (createdId === -1) {
      return -1;
    }

    setExerciseId(createdId);
    setExerciseCreated(true);

    return createdId;
  };

  const createExercise = async () => {
    try {
      let createdId = await exerciseContext.create(exerciseData());
      return createdId === undefined ? -1 : createdId;
    } catch (e) {
      console.warn(e);
      alert(t("exerciseSaveFailed"));
      return -1;
    }
  };

  const openSampleSolutionEditor = async () => {
    let id = await ensureExerciseExists();
    if (id === -1) return;

    modelEditorModalContext.setModelingLanguage(language);
    modelEditorModalContext.setType("sampleSolution");
    modelEditorModalContext.setExerciseId(id.toString());
    modelEditorModalContext.toggleModal();
  };

  const goBackToAssignment = () => {
    navigate("/assignments/" + props.assignmentId);
  };

  const saveExercise = async () => {
    if (emptyCheck()) {
      alert("Please make sure that all fields have been filled out!");
      return;
    }

    if (activeModels.length === 0) {
      if (!window.confirm(t("noSampleSolutionAdded"))) return;
    }

    if (exerciseCreated) {
      exerciseContext.update(exerciseId, exerciseData());
    } else {
      let createdId = await createExercise();
      if (createdId === -1) return;
    }

    navigate("/assignments/" + props.assignmentId);
  };
  return (
    <div>
      <NameField activeExercise={activeExercise} h={handleChange} />
      <LanguageField language={language} h={handleChangeLanguage} />
      <DescriptionField activeExercise={activeExercise} h={handleChangeDescription} tooltipKey="descriptionCreationTooltip" />
      <ScenarioField activeExercise={activeExercise} h={handleChangeScenario} />
      <SchemaField activeExercise={activeExercise} h={handleChange} />
      <FeedUpField activeExercise={activeExercise} h={handleChange} />
      <FeedForwardPosField activeExercise={activeExercise} h={handleChange} />
      <FeedForwardNegField activeExercise={activeExercise} h={handleChange} />
      <FeedForwardThresholdField activeExercise={activeExercise} h={handleChange} />
      <div className="field">
        <div className="label">
          {t("autoAssessmentEx")}
          <Tooltip
            icon="circle-info"
            class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-top"
            content={t("autoAssessmentExDesc")}
          ></Tooltip>
        </div>
        <input
          name="directFeedback"
          type="checkbox"
          checked={activeExercise.directFeedback}
          onChange={handleCheckboxChange}
        />
      </div>
      <ScoreField maxScore={maxScore} setMaxScore={setMaxScore} />
      <div className="field">
        <label className="label">
          {t("sampleSolution")}
          <Tooltip
            icon="circle-info"
            class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-top"
            content={t("samplesolutionsTooltip")}
          ></Tooltip>
        </label>
        {exerciseCreated ? (
          <ModelList
            modelList={activeModels}
            modelingLanguage={language}
            exerciseId={exerciseId}
            exerciseType={"creation"}
            modelType={"sampleSolution"}
          />
        ) : (
          <div className="section">
            <div className="columns is-multiline is-centered">
              <AddModelCard openModellingTool={openSampleSolutionEditor} />
            </div>
          </div>
        )}
      </div>
      <div className="columns mt-4">
        <button className="button is-normal is-success" onClick={saveExercise}>
          {t("saveExercise")}
        </button>
        <button className="button is-danger" onClick={goBackToAssignment}>
          {t("cancel")}
        </button>
      </div>
    </div>
  );
};
